import { isLosslessNumber, stringify as stringifyLosslessJson } from 'lossless-json'
import { ApiError } from './http'
import type {
  SelectedDealModelDetail as SelectedDealModel,
  DealModelCapabilities,
  DealModelCatalogue,
  DealModelCatalogueField,
  DealModelCloneOutcome,
  DealModelDiagnostic,
  DealModelCurrent,
  DealModelFixture,
  DealModelFixtureDocument,
  DealModelFixtureList,
  DealModelList,
  DealModelPreview,
  DealModelResetOutcome,
  DealModelSettings,
  DealModelSummary,
  DealModelUserSettings,
  DealModelValueDescriptor,
  PropertyDealModelChoice,
} from './dealModelTypes'

type JsonRecord = Record<string, unknown>

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const SHA256 = /^[0-9a-f]{64}$/
const INTEGER_TOKEN = /^(?:0|-[1-9][0-9]*|[1-9][0-9]*)$/
const CANONICAL_NUMBER_TOKEN = /^(?:0|-?[1-9][0-9]*|-?(?:0|[1-9][0-9]*)\.[0-9]*[1-9])$/
const POINTER = /^(?:\/(?:[^~/]|~[01])*)*$/
// 'money' is GBP decimal to two places and is the only money kind the current
// composer writes.
const SEMANTIC_TYPES = ['money', 'moneyPence', 'decimal', 'ratio', 'percentage', 'integer', 'count', 'months', 'days', 'areaSqm', 'boolean', 'enum', 'string', 'date'] as const

export const DEAL_MODEL_SOURCE_CONTRACT_VERSION = 'auction-hub-deal-model-source-v0.2'
export const DEAL_MODEL_MONEY_DECIMAL_PLACES = 2
/// The approved GBP fixture tolerance, in pounds: exactly one penny.
export const DEAL_MODEL_APPROVED_MONEY_TOLERANCE = '0.01'

/// The v0.2 optional display unit on an input or a calc row. It is presentation
/// only: nothing about it reaches an expression or the compiled bundle.
export const DEAL_MODEL_UNIT_MAX_LENGTH = 40

/// The most tiers a v0.2 `outcomes.main` may declare, the authored default arm
/// included. Four, so the built-ins' good / average / poor / kill vocabulary
/// fits. The vocabulary itself is a convention of the built-ins, not a schema
/// enum: an author names their own tiers. Mirrors MAX_OUTCOME_TIERS in
/// crates/deal-model/src/contract.rs and `maxItems: 4` on
/// `#/$defs/outcomeMain/properties/tiers` in the v0.2 source schema.
export const DEAL_MODEL_MAX_OUTCOME_TIERS = 4

/// Group item kinds in the current source contract.
export const DEAL_MODEL_GROUP_ITEM_KINDS_V0_2 = ['row', 'input', 'blockInstance'] as const
export type DealModelGroupItemKind = (typeof DEAL_MODEL_GROUP_ITEM_KINDS_V0_2)[number]

/// The source collection a group item of each kind resolves against, mirroring
/// crates/deal-model/src/contract.rs.
export const DEAL_MODEL_GROUP_ITEM_COLLECTIONS: Record<DealModelGroupItemKind, 'rows' | 'inputs' | 'blockInstances'> = {
  row: 'rows',
  input: 'inputs',
  blockInstance: 'blockInstances',
}

export function dealModelGroupItemKinds(contractVersion: string): readonly DealModelGroupItemKind[] {
  if (contractVersion !== DEAL_MODEL_SOURCE_CONTRACT_VERSION) mismatch(`Unsupported Deal Model source contract ${contractVersion}`)
  return DEAL_MODEL_GROUP_ITEM_KINDS_V0_2
}

export const DEAL_MODEL_RESOURCE_LIMITS = {
  sourceUtf8Bytes: 1_000_000,
  zenExpressionUtf8Bytes: 4_000,
  evaluationInputUtf8Bytes: 524_288,
  evaluationOutputUtf8Bytes: 524_288,
} as const

function mismatch(message: string): never {
  throw new ApiError(502, 'deal_model_contract_mismatch', message)
}

function ownKeys(value: JsonRecord): string[] {
  return Object.keys(value).sort()
}

export function closedRecord(value: unknown, field: string, keys: readonly string[]): JsonRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value) || isLosslessNumber(value)) mismatch(`${field} must be an object`)
  const data = value as JsonRecord
  const expected = [...keys].sort()
  const actual = ownKeys(data)
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) {
    mismatch(`${field} fields are invalid: expected ${expected.join(', ')}, received ${actual.join(', ')}`)
  }
  return data
}

function objectValue(value: unknown, field: string): JsonRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value) || isLosslessNumber(value)) mismatch(`${field} must be an object`)
  return value as JsonRecord
}

function arrayValue(value: unknown, field: string): unknown[] {
  if (!Array.isArray(value)) mismatch(`${field} must be an array`)
  return value
}

function stringValue(value: unknown, field: string, allowEmpty = false): string {
  if (typeof value !== 'string' || (!allowEmpty && value.length === 0)) mismatch(`${field} must be a${allowEmpty ? '' : ' non-empty'} string`)
  return value
}

function keyValue(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (parsed.length > 100 || !/^[a-z][a-z0-9]*(?:[A-Z][A-Za-z0-9]*|[._-][a-z0-9]+)*$/.test(parsed)) mismatch(`${field} must be a canonical source key`)
  return parsed
}

function nullableString(value: unknown, field: string): string | null {
  return value === null ? null : stringValue(value, field)
}

function booleanValue(value: unknown, field: string): boolean {
  if (typeof value !== 'boolean') mismatch(`${field} must be a boolean`)
  return value
}

function enumValue<T extends string>(value: unknown, field: string, values: readonly T[]): T {
  const parsed = stringValue(value, field)
  if (!values.includes(parsed as T)) mismatch(`${field} is invalid`)
  return parsed as T
}

function uuid(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (!UUID.test(parsed)) mismatch(`${field} must be a canonical lowercase UUID`)
  return parsed
}

export function assertCanonicalUuid(value: unknown, field: string): string {
  return uuid(value, field)
}

function nullableUuid(value: unknown, field: string): string | null {
  return value === null ? null : uuid(value, field)
}

function sha256(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (!SHA256.test(parsed)) mismatch(`${field} must be a lowercase SHA-256 hex identity`)
  return parsed
}

function nullableSha256(value: unknown, field: string): string | null {
  return value === null ? null : sha256(value, field)
}

function timestamp(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(parsed) || Number.isNaN(Date.parse(parsed))) mismatch(`${field} must be an RFC 3339 UTC timestamp`)
  return parsed
}

function pointer(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (!POINTER.test(parsed)) mismatch(`${field} must be an RFC 6901 object pointer`)
  return parsed
}

function safeInteger(value: unknown, field: string, minimum = Number.MIN_SAFE_INTEGER): number {
  const token = isLosslessNumber(value) ? value.toString() : typeof value === 'number' ? String(value) : ''
  if (!INTEGER_TOKEN.test(token)) mismatch(`${field} must be a safe integer`)
  const parsed = Number(token)
  if (!Number.isSafeInteger(parsed) || parsed < minimum) mismatch(`${field} must be a safe integer at least ${minimum}`)
  return parsed
}

function nullableSafeInteger(value: unknown, field: string, minimum = Number.MIN_SAFE_INTEGER): number | null {
  return value === null ? null : safeInteger(value, field, minimum)
}

function signedI64JsonNumber(value: unknown, field: string): unknown {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) mismatch(`${field} must not arrive through an unsafe host Number`)
  const token = isLosslessNumber(value) ? value.toString() : typeof value === 'number' ? String(value) : ''
  if (!INTEGER_TOKEN.test(token)) mismatch(`${field} must be an integral JSON number token`)
  const integer = BigInt(token)
  if (integer < -9223372036854775808n || integer > 9223372036854775807n) mismatch(`${field} is outside signed i64 range`)
  return value
}

export function canonicalNumberToken(value: string): boolean {
  return CANONICAL_NUMBER_TOKEN.test(value)
}

export function assertJsonUtf8Ceiling<T>(value: T, field: string, maximumBytes: number): T {
  let encoded: string | undefined
  try { encoded = stringifyLosslessJson(value) }
  catch { mismatch(`${field} must be a losslessly serializable JSON document`) }
  if (typeof encoded !== 'string') mismatch(`${field} must be a losslessly serializable JSON document`)
  const bytes = new TextEncoder().encode(encoded).byteLength
  if (bytes > maximumBytes) mismatch(`${field} is ${bytes} UTF-8 bytes and exceeds the ${maximumBytes}-byte frozen resource ceiling`)
  return value
}

const ZEN_EXPRESSION_FIELDS = new Set(['expressionZen', 'statusZen', 'explanationZen', 'conditionZen', 'passConditionZen', 'probeStatusZen', 'lowerBoundZen', 'upperBoundZen'])

/// The optional v0.2 `composerAnnotations` array: the standalone comments and
/// blank lines the composer's text view prints between rows. Presentation only —
/// the compiler never reads it — but it is still contract, so it is checked here
/// rather than discovered at the server. Mirrors validate_composer_annotations in
/// crates/deal-model/src/contract.rs and the v0.2 JSON schema's size bounds.
export const DEAL_MODEL_COMPOSER_ANNOTATION_KINDS = ['comment', 'blank'] as const
export type DealModelComposerAnnotationKind = (typeof DEAL_MODEL_COMPOSER_ANNOTATION_KINDS)[number]
export const DEAL_MODEL_COMPOSER_ANNOTATION_LIMITS = { maxItems: 200, textMaxLength: 500 } as const

export interface DealModelComposerAnnotation {
  id: string
  kind: DealModelComposerAnnotationKind
  /// null anchors the annotation before the first row; otherwise it prints
  /// immediately after the input, calc row or block instance it names.
  afterRowId: string | null
  text?: string
}

export function assertDealModelComposerAnnotations(value: unknown, field = 'composerAnnotations'): DealModelComposerAnnotation[] {
  const items = arrayValue(value, field)
  if (items.length > DEAL_MODEL_COMPOSER_ANNOTATION_LIMITS.maxItems) mismatch(`${field} carries ${items.length} entries and exceeds the ${DEAL_MODEL_COMPOSER_ANNOTATION_LIMITS.maxItems}-entry ceiling`)
  const seen = new Set<string>()
  return items.map((item, index) => {
    const at = `${field}[${index}]`
    const record = objectValue(item, at)
    for (const key of ownKeys(record)) if (!['id', 'kind', 'afterRowId', 'text'].includes(key)) mismatch(`${at} declares an unexpected field ${key}`)
    for (const key of ['id', 'kind', 'afterRowId']) if (!(key in record)) mismatch(`${at}.${key} is required`)
    const id = uuid(record.id, `${at}.id`)
    if (seen.has(id)) mismatch(`${at}.id is declared more than once`)
    seen.add(id)
    const kind = enumValue(record.kind, `${at}.kind`, DEAL_MODEL_COMPOSER_ANNOTATION_KINDS)
    const afterRowId = nullableUuid(record.afterRowId, `${at}.afterRowId`)
    if (kind === 'blank') {
      if (record.text !== undefined) mismatch(`${at}.text is legal only on a comment`)
      return { id, kind, afterRowId }
    }
    const text = stringValue(record.text, `${at}.text`)
    if (text.trim().length === 0) mismatch(`${at}.text must be a non-blank comment`)
    if ([...text].length > DEAL_MODEL_COMPOSER_ANNOTATION_LIMITS.textMaxLength) mismatch(`${at}.text must be at most ${DEAL_MODEL_COMPOSER_ANNOTATION_LIMITS.textMaxLength} characters`)
    if (/[\u0000-\u001f\u007f]/.test(text)) mismatch(`${at}.text must be a single line`)
    return { id, kind, afterRowId, text }
  })
}

/// A v0.2 main outcome declares one to DEAL_MODEL_MAX_OUTCOME_TIERS ordered
/// tiers, first match wins, and only the last may be the default
/// (`conditionZen: null`). Refusing a bad one here means the composer says so
/// before a save, rather than the server answering 422. Mirrors
/// validate_outcomes in crates/deal-model/src/contract.rs.
export function assertDealModelOutcomeTiers(outcomes: unknown, field = 'outcomes'): void {
  if (outcomes === undefined || outcomes === null) return
  const block = objectValue(outcomes, field)
  if (block.main === undefined || block.main === null) return
  const main = objectValue(block.main, `${field}.main`)
  const tiers = arrayValue(main.tiers, `${field}.main.tiers`)
  if (tiers.length === 0 || tiers.length > DEAL_MODEL_MAX_OUTCOME_TIERS) {
    mismatch(`${field}.main.tiers declares one to ${DEAL_MODEL_MAX_OUTCOME_TIERS} ordered tiers, the default arm included; ${tiers.length} are declared`)
  }
  tiers.forEach((tier, index) => {
    const condition = objectValue(tier, `${field}.main.tiers[${index}]`).conditionZen
    if (condition === null && index !== tiers.length - 1) mismatch(`${field}.main.tiers[${index}] is the default arm but is not last`)
  })
}

export function assertSourceDocumentResources<T>(value: T, field = 'sourceDocument'): T {
  const inspect = (node: unknown, path: string): void => {
    if (Array.isArray(node)) return node.forEach((item, index) => inspect(item, `${path}[${index}]`))
    if (!node || typeof node !== 'object' || isLosslessNumber(node)) return
    for (const [key, child] of Object.entries(node as JsonRecord)) {
      // A v0.2 outcome tier declares its default arm as `conditionZen: null`
      // — the frozen schema types the field `string | null` — so a null here is
      // the contract saying "no condition", not a malformed expression.
      if (ZEN_EXPRESSION_FIELDS.has(key) && child !== null) {
        const expression = stringValue(child, `${path}.${key}`, true)
        const bytes = new TextEncoder().encode(expression).byteLength
        if (bytes > DEAL_MODEL_RESOURCE_LIMITS.zenExpressionUtf8Bytes) mismatch(`${path}.${key} exceeds the ${DEAL_MODEL_RESOURCE_LIMITS.zenExpressionUtf8Bytes}-byte frozen ZEN expression ceiling`)
      }
      inspect(child, `${path}.${key}`)
    }
  }
  inspect(value, field)
  // Refusing a bad current-source document here means a save fails in the composer,
  // where the author can see why, rather than as a 422 from the server.
  if (value && typeof value === 'object' && !Array.isArray(value) && !isLosslessNumber(value)) {
    const document = value as JsonRecord
    if (document.contractVersion !== DEAL_MODEL_SOURCE_CONTRACT_VERSION) mismatch(`${field}.contractVersion must be ${DEAL_MODEL_SOURCE_CONTRACT_VERSION}`)
    if (document.composerAnnotations !== undefined) {
      assertDealModelComposerAnnotations(document.composerAnnotations, `${field}.composerAnnotations`)
    }
    assertDealModelOutcomeTiers(document.outcomes, `${field}.outcomes`)
  }
  return assertJsonUtf8Ceiling(value, field, DEAL_MODEL_RESOURCE_LIMITS.sourceUtf8Bytes)
}

export function assertCanonicalNumberToken(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (!canonicalNumberToken(parsed)) mismatch(`${field} must equal auction-hub-canonical-json-v1 canonicalNumberToken output`)
  return parsed
}

/// GBP money on the wire: a canonical number token carrying at most two decimal
/// places. Pounds, never pence, and never a host float.
export function assertGbpMoneyToken(value: unknown, field: string): string {
  const parsed = assertCanonicalNumberToken(value, field)
  const fraction = parsed.split('.')[1] ?? ''
  if (fraction.length > DEAL_MODEL_MONEY_DECIMAL_PLACES) mismatch(`${field} must be GBP with at most ${DEAL_MODEL_MONEY_DECIMAL_PLACES} decimal places`)
  return parsed
}

/// A v0.2 display unit: a short, non-blank, single-line label of at most
/// DEAL_MODEL_UNIT_MAX_LENGTH characters. Mirrors validate_display_unit in
/// crates/deal-model/src/contract.rs.
export function assertDealModelUnit(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (parsed.trim().length === 0) mismatch(`${field} must be a non-blank unit label`)
  if ([...parsed].length > DEAL_MODEL_UNIT_MAX_LENGTH) mismatch(`${field} must be at most ${DEAL_MODEL_UNIT_MAX_LENGTH} characters`)
  if (/[\u0000-\u001f\u007f]/.test(parsed)) mismatch(`${field} must be a single-line unit label`)
  return parsed
}

export function assertSignedI64Token(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (!INTEGER_TOKEN.test(parsed)) mismatch(`${field} must be a canonical signed integer string`)
  const integer = BigInt(parsed)
  if (integer < -9223372036854775808n || integer > 9223372036854775807n) mismatch(`${field} is outside signed i64 range`)
  return parsed
}

export function assertUnsignedI64Token(value: unknown, field: string): string {
  const parsed = assertSignedI64Token(value, field)
  if (parsed.startsWith('-')) mismatch(`${field} must be non-negative`)
  return parsed
}

export function assertPositiveRevision(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (!/^[1-9][0-9]*$/.test(parsed)) mismatch(`${field} must be a canonical positive integral revision string`)
  const integer = BigInt(parsed)
  if (integer > 9223372036854775807n) mismatch(`${field} is outside signed i64 range`)
  return parsed
}

export function assertSettingsRevision(value: unknown, field: string): string {
  const parsed = stringValue(value, field)
  if (!/^(?:0|[1-9][0-9]*)$/.test(parsed)) mismatch(`${field} must be a canonical non-negative integral settings revision string`)
  if (BigInt(parsed) > 9223372036854775807n) mismatch(`${field} is outside signed i64 range`)
  return parsed
}

export function validateSemanticValue(value: unknown, descriptor: DealModelValueDescriptor, field: string): string | boolean | null {
  if (!SEMANTIC_TYPES.includes(descriptor.semanticType)) mismatch(`${field} has an unsupported semantic type`)
  if (value === null) {
    if (!descriptor.nullable) mismatch(`${field} is not nullable`)
    return null
  }
  if (descriptor.semanticType === 'boolean') return booleanValue(value, field)
  if (['moneyPence', 'integer'].includes(descriptor.semanticType)) return assertSignedI64Token(value, field)
  if (['count', 'months', 'days'].includes(descriptor.semanticType)) return assertUnsignedI64Token(value, field)
  if (descriptor.semanticType === 'money') return assertGbpMoneyToken(value, field)
  if (['decimal', 'ratio', 'percentage', 'areaSqm'].includes(descriptor.semanticType)) {
    const parsed = assertCanonicalNumberToken(value, field)
    if (descriptor.semanticType === 'areaSqm' && parsed.startsWith('-')) mismatch(`${field} must be non-negative`)
    return parsed
  }
  if (descriptor.semanticType === 'date') {
    const parsed = stringValue(value, field)
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(parsed)
    if (!match) mismatch(`${field} must be an ISO date`)
    const year = Number(match[1]), month = Number(match[2]), day = Number(match[3]), date = new Date(Date.UTC(year, month - 1, day))
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) mismatch(`${field} must be a calendar-valid ISO date`)
    return parsed
  }
  const parsed = stringValue(value, field)
  if (/[\u0000-\u001f\u007f]/.test(parsed)) mismatch(`${field} contains a forbidden control character`)
  if (descriptor.semanticType === 'enum' && !descriptor.enumValues.includes(parsed)) mismatch(`${field} is outside its declared enum`)
  return parsed
}

function valueDescriptor(value: unknown, field: string): DealModelValueDescriptor {
  const data = closedRecord(value, field, ['key', 'semanticType', 'nullable', 'enumValues'])
  const semanticType = enumValue(data.semanticType, `${field}.semanticType`, SEMANTIC_TYPES)
  const enumValues = arrayValue(data.enumValues, `${field}.enumValues`).map((item, index) => stringValue(item, `${field}.enumValues[${index}]`))
  if (semanticType !== 'enum' && enumValues.length > 0) mismatch(`${field}.enumValues is legal only for enum`)
  if (semanticType === 'enum' && enumValues.length === 0) mismatch(`${field}.enumValues must not be empty`)
  return { key: stringValue(data.key, `${field}.key`), semanticType, nullable: booleanValue(data.nullable, `${field}.nullable`), enumValues }
}

function exactScalarRecord(value: unknown, descriptors: DealModelValueDescriptor[], field: string): Record<string, string | boolean | null> {
  const data = objectValue(value, field)
  const descriptorKeys = descriptors.map((descriptor) => descriptor.key)
  if (new Set(descriptorKeys).size !== descriptorKeys.length) mismatch(`${field} descriptors contain duplicate keys`)
  const actual = Object.keys(data).sort(), expected = [...descriptorKeys].sort()
  if (actual.length !== expected.length || actual.some((key, index) => key !== expected[index])) mismatch(`${field} keys do not exactly match descriptors`)
  return Object.fromEntries(descriptors.map((descriptor) => [descriptor.key, validateSemanticValue(data[descriptor.key], descriptor, `${field}.${descriptor.key}`)]))
}

// User defaults are overrides: absent keys inherit each saved model's fallback.
// Present keys retain the same exact type and entitlement checks as full records.
function sparseScalarRecord(value: unknown, descriptors: DealModelValueDescriptor[], field: string): Record<string, string | boolean | null> {
  const data = objectValue(value, field)
  const byKey = new Map(descriptors.map(descriptor => [descriptor.key, descriptor]))
  if (byKey.size !== descriptors.length) mismatch(`${field} descriptors contain duplicate keys`)
  return Object.fromEntries(Object.entries(data).map(([key, item]) => {
    const descriptor = byKey.get(key)
    if (!descriptor) mismatch(`${field}.${key} is not an available setting`)
    return [key, validateSemanticValue(item, descriptor, `${field}.${key}`)]
  }))
}

function contractVersion(data: JsonRecord, expected: string, field: string): void {
  if (data.contractVersion !== expected) mismatch(`${field}.contractVersion must be ${expected}`)
}

export function envelopeData(value: unknown, field: string): JsonRecord {
  return closedRecord(closedRecord(value, `${field} envelope`, ['data']).data, `${field}.data`, ownKeys(objectValue((value as JsonRecord).data, `${field}.data`)))
}

const MODEL_CAPABILITY_NAMES = ['read', 'use', 'edit', 'test', 'operate'] as const

function capabilities(value: unknown, field: string): DealModelCapabilities {
  // `reset` is the newest capability and the only optional one: a server that
  // predates Reset to built-in omits it, and its absence reads as denied.
  // Present or absent, the record stays closed — an unknown key is still a
  // contract mismatch.
  const present = objectValue(value, field)
  const names = 'reset' in present ? [...MODEL_CAPABILITY_NAMES, 'reset'] : MODEL_CAPABILITY_NAMES
  const data = closedRecord(value, field, names)
  return {
    read: booleanValue(data.read, `${field}.read`),
    use: booleanValue(data.use, `${field}.use`),
    edit: booleanValue(data.edit, `${field}.edit`),
    test: booleanValue(data.test, `${field}.test`),
    operate: booleanValue(data.operate, `${field}.operate`),
    reset: data.reset === undefined ? false : booleanValue(data.reset, `${field}.reset`),
  }
}

function summary(value: unknown, field: string): DealModelSummary {
  const data = closedRecord(value, field, ['logicalModelId', 'name', 'description', 'owner', 'visibility', 'sourceKind', 'state', 'revision', 'sourceSha256', 'updatedAt', 'capabilities'])
  return {
    logicalModelId: uuid(data.logicalModelId, `${field}.logicalModelId`),
    name: stringValue(data.name, `${field}.name`),
    description: stringValue(data.description, `${field}.description`, true),
    owner: stringValue(data.owner, `${field}.owner`),
    visibility: enumValue(data.visibility, `${field}.visibility`, ['system', 'private', 'shared']),
    sourceKind: enumValue(data.sourceKind, `${field}.sourceKind`, ['system', 'user']),
    state: enumValue(data.state, `${field}.state`, ['ready', 'inactive', 'invalid']),
    revision: assertPositiveRevision(data.revision, `${field}.revision`),
    sourceSha256: sha256(data.sourceSha256, `${field}.sourceSha256`),
    updatedAt: timestamp(data.updatedAt, `${field}.updatedAt`),
    capabilities: capabilities(data.capabilities, `${field}.capabilities`),
  }
}

function diagnostic(value: unknown, field: string): DealModelDiagnostic {
  const data = closedRecord(value, field, ['code', 'severity', 'message', 'sourceId', 'pointer', 'line', 'column'])
  return {
    code: stringValue(data.code, `${field}.code`),
    severity: enumValue(data.severity, `${field}.severity`, ['error', 'warning', 'info']),
    message: stringValue(data.message, `${field}.message`),
    sourceId: nullableUuid(data.sourceId, `${field}.sourceId`),
    pointer: data.pointer === null ? null : pointer(data.pointer, `${field}.pointer`),
    line: nullableSafeInteger(data.line, `${field}.line`, 1),
    column: nullableSafeInteger(data.column, `${field}.column`, 1),
  }
}

function catalogueField(value: unknown, field: string, output = false): DealModelCatalogueField | DealModelCatalogue['outputFields'][number] {
  const keys = ['sourceId', 'key', 'label', 'pointer', 'semanticType', 'unit', 'nullable', 'scope', ...(output ? ['writerId'] : ['provenanceKind'])]
  const data = closedRecord(value, field, keys)
  const common = {
    sourceId: uuid(data.sourceId, `${field}.sourceId`), key: stringValue(data.key, `${field}.key`), label: stringValue(data.label, `${field}.label`),
    pointer: pointer(data.pointer, `${field}.pointer`), semanticType: enumValue(data.semanticType, `${field}.semanticType`, SEMANTIC_TYPES), unit: nullableString(data.unit, `${field}.unit`),
    nullable: booleanValue(data.nullable, `${field}.nullable`), scope: stringValue(data.scope, `${field}.scope`),
  }
  return output
    ? { ...common, writerId: uuid(data.writerId, `${field}.writerId`) }
    : { ...common, provenanceKind: stringValue(data.provenanceKind, `${field}.provenanceKind`) }
}

function fixture(value: unknown, field: string): DealModelFixture {
  const data = closedRecord(value, field, ['fixtureId', 'key', 'label', 'ordinal', 'document'])
  const document = fixtureDocument(data.document, `${field}.document`)
  const fixtureId = uuid(data.fixtureId, `${field}.fixtureId`)
  const key = keyValue(data.key, `${field}.key`)
  const label = stringValue(data.label, `${field}.label`)
  if (document.id !== fixtureId || document.key !== key || document.label !== label) mismatch(`${field} record identity must equal its fixture document identity`)
  return { fixtureId, key, label, ordinal: safeInteger(data.ordinal, `${field}.ordinal`, 1), document }
}

function fixtureDocument(value: unknown, field: string): DealModelFixtureDocument {
  const data = closedRecord(value, field, ['id', 'key', 'label', 'resolvedInputs', 'scenarioIds', 'expected', 'comparison'])
  const expected = closedRecord(data.expected, `${field}.expected`, ['calculationState', 'resultSubset', 'missingInputIds', 'checks', 'verdictValue', 'diagnosticCodes'])
  const comparisonData = objectValue(data.comparison, `${field}.comparison`)
  const mode = enumValue(comparisonData.mode, `${field}.comparison.mode`, ['exact', 'approvedPenceTolerance', 'approvedMoneyTolerance'] as const)
  const comparisonKeys = mode === 'exact'
    ? ['mode']
    : mode === 'approvedPenceTolerance'
      ? ['approvalReference', 'mode', 'tolerancePence']
      : ['approvalReference', 'mode', 'toleranceMoney']
  const comparison = closedRecord(data.comparison, `${field}.comparison`, comparisonKeys)
  const tolerancePence = mode === 'approvedPenceTolerance' ? signedI64JsonNumber(comparison.tolerancePence, `${field}.comparison.tolerancePence`) : undefined
  if (mode === 'approvedPenceTolerance' && (isLosslessNumber(tolerancePence) ? tolerancePence.toString() : String(tolerancePence)) !== '1') mismatch(`${field}.comparison.tolerancePence must be exactly 1`)
  // The GBP tolerance is the same single penny, written in pounds.
  const toleranceMoney = mode === 'approvedMoneyTolerance' ? comparison.toleranceMoney : undefined
  if (mode === 'approvedMoneyTolerance' && (isLosslessNumber(toleranceMoney) ? toleranceMoney.toString() : String(toleranceMoney)) !== DEAL_MODEL_APPROVED_MONEY_TOLERANCE) mismatch(`${field}.comparison.toleranceMoney must be exactly ${DEAL_MODEL_APPROVED_MONEY_TOLERANCE}`)
  const parsedComparison: DealModelFixtureDocument['comparison'] = mode === 'exact'
    ? { mode }
    : mode === 'approvedPenceTolerance'
      ? { mode, tolerancePence, approvalReference: stringValue(comparison.approvalReference, `${field}.comparison.approvalReference`) }
      : { mode, toleranceMoney, approvalReference: stringValue(comparison.approvalReference, `${field}.comparison.approvalReference`) }
  const parsed: DealModelFixtureDocument = {
    id: uuid(data.id, `${field}.id`), key: keyValue(data.key, `${field}.key`), label: stringValue(data.label, `${field}.label`),
    resolvedInputs: objectValue(data.resolvedInputs, `${field}.resolvedInputs`),
    scenarioIds: arrayValue(data.scenarioIds, `${field}.scenarioIds`).map((item, index) => uuid(item, `${field}.scenarioIds[${index}]`)),
    expected: {
      calculationState: enumValue(expected.calculationState, `${field}.expected.calculationState`, ['computed', 'notComputable', 'evaluationFailed']),
      resultSubset: objectValue(expected.resultSubset, `${field}.expected.resultSubset`),
      missingInputIds: arrayValue(expected.missingInputIds, `${field}.expected.missingInputIds`).map((item, index) => uuid(item, `${field}.expected.missingInputIds[${index}]`)),
      checks: arrayValue(expected.checks, `${field}.expected.checks`).map((item, index) => { const row = closedRecord(item, `${field}.expected.checks[${index}]`, ['checkId', 'status', 'reasonCode']); return { checkId: uuid(row.checkId, `${field}.expected.checks[${index}].checkId`), status: enumValue(row.status, `${field}.expected.checks[${index}].status`, ['pass', 'tight', 'fail', 'unknown', 'notApplicable']), reasonCode: keyValue(row.reasonCode, `${field}.expected.checks[${index}].reasonCode`) } }),
      verdictValue: nullableString(expected.verdictValue, `${field}.expected.verdictValue`),
      diagnosticCodes: arrayValue(expected.diagnosticCodes, `${field}.expected.diagnosticCodes`).map((item, index) => stringValue(item, `${field}.expected.diagnosticCodes[${index}]`)),
    },
    comparison: parsedComparison,
  }
  assertJsonUtf8Ceiling(parsed.resolvedInputs, `${field}.resolvedInputs`, DEAL_MODEL_RESOURCE_LIMITS.evaluationInputUtf8Bytes)
  assertJsonUtf8Ceiling(parsed.expected.resultSubset, `${field}.expected.resultSubset`, DEAL_MODEL_RESOURCE_LIMITS.evaluationOutputUtf8Bytes)
  return assertJsonUtf8Ceiling(parsed, field, DEAL_MODEL_RESOURCE_LIMITS.sourceUtf8Bytes)
}

export function validateFixtureDocument(value: unknown, field = 'fixture'): DealModelFixtureDocument {
  return fixtureDocument(value, field)
}

export function parseDealModelList(value: unknown): DealModelList {
  const data = closedRecord(envelopeData(value, 'deal models'), 'deal models.data', ['contractVersion', 'items'])
  contractVersion(data, 'deal-model-list-v1', 'deal models')
  return { contractVersion: 'deal-model-list-v1', items: arrayValue(data.items, 'deal models.items').map((item, index) => summary(item, `deal models.items[${index}]`)) }
}

export function parseDealModelDetail(value: unknown): DealModelSummary { return summary(envelopeData(value, 'deal model'), 'deal model.data') }

function assertDraftEntitySourceProjection(sourceDocument: unknown, entities: DealModelCurrent['entities']): void {
  const source = objectValue(sourceDocument, 'deal model current.sourceDocument')
  const collectionsByKind: Record<DealModelCurrent['entities'][number]['kind'], readonly string[]> = {
    input: ['inputs'],
    formula: ['rows'],
    output: ['rows'],
    decision: ['checks'],
    dependency: ['blockInstances', 'modelDependencies'],
    // v0.2 outcomes.main is a single record, not a collection, so it is
    // projected below rather than searched for among array records.
    outcome: [],
  }
  const projectedRecords = new Set<string>()

  for (const [entityIndex, entity] of entities.entries()) {
    if (entity.kind === 'outcome') {
      const outcomes = source.outcomes === undefined ? {} : objectValue(source.outcomes, 'deal model current.sourceDocument.outcomes')
      const main = outcomes.main === null || outcomes.main === undefined ? null : objectValue(outcomes.main, 'deal model current.sourceDocument.outcomes.main')
      if (!main || main.rowId !== entity.id || main.key !== entity.key) mismatch(`deal model current.entities[${entityIndex}] must map bijectively by id, key and kind to one source record`)
      if (projectedRecords.has('outcomes.main')) mismatch(`deal model current.entities[${entityIndex}] duplicates another source-record projection`)
      projectedRecords.add('outcomes.main')
      continue
    }
    const candidates = collectionsByKind[entity.kind].flatMap((collectionName) => {
      const collection = source[collectionName] === undefined
        ? []
        : arrayValue(source[collectionName], `deal model current.sourceDocument.${collectionName}`)
      return collection.map((item, recordIndex) => ({
        collectionName,
        recordIndex,
        record: objectValue(item, `deal model current.sourceDocument.${collectionName}[${recordIndex}]`),
      }))
    })
    const idMatches = candidates.filter(({ record }) => record.id === entity.id)
    const keyMatches = candidates.filter(({ record }) => record.key === entity.key)
    const idMatch = idMatches[0]
    const keyMatch = keyMatches[0]
    if (idMatches.length !== 1 || keyMatches.length !== 1 || !idMatch || !keyMatch || idMatch.collectionName !== keyMatch.collectionName || idMatch.recordIndex !== keyMatch.recordIndex) {
      mismatch(`deal model current.entities[${entityIndex}] must map bijectively by id, key and kind to one source record`)
    }
    const projection = `${idMatch!.collectionName}\u0000${idMatch!.recordIndex}`
    if (projectedRecords.has(projection)) mismatch(`deal model current.entities[${entityIndex}] duplicates another source-record projection`)
    projectedRecords.add(projection)
  }
}

export function parseDealModelCurrent(value: unknown): DealModelCurrent {
  const data = closedRecord(envelopeData(value, 'deal model current'), 'deal model current.data', ['contractVersion', 'logicalModelId', 'revision', 'sourceSha256', 'sourceDocument', 'entities', 'selectedEntityId', 'diagnostics', 'capabilities'])
  contractVersion(data, 'deal-model-current-v1', 'deal model current')
  const entities = arrayValue(data.entities, 'deal model current.entities').map((item, index) => {
    const entity = closedRecord(item, `deal model current.entities[${index}]`, ['id', 'key', 'label', 'kind', 'parentId'])
    return { id: uuid(entity.id, `entities[${index}].id`), key: stringValue(entity.key, `entities[${index}].key`), label: stringValue(entity.label, `entities[${index}].label`), kind: enumValue(entity.kind, `entities[${index}].kind`, ['input', 'formula', 'decision', 'output', 'dependency', 'outcome']), parentId: nullableUuid(entity.parentId, `entities[${index}].parentId`) }
  })
  const sourceDocument = assertSourceDocumentResources(data.sourceDocument, 'deal model current.sourceDocument')
  assertDraftEntitySourceProjection(sourceDocument, entities)
  const selectedEntityId = nullableUuid(data.selectedEntityId, 'deal model current.selectedEntityId')
  if (selectedEntityId && !entities.some((entity) => entity.id === selectedEntityId)) mismatch('deal model current.selectedEntityId does not name an entity')
  return { contractVersion: 'deal-model-current-v1', logicalModelId: uuid(data.logicalModelId, 'deal model current.logicalModelId'), revision: assertPositiveRevision(data.revision, 'deal model current.revision'), sourceSha256: sha256(data.sourceSha256, 'deal model current.sourceSha256'), sourceDocument, entities, selectedEntityId, diagnostics: arrayValue(data.diagnostics, 'deal model current.diagnostics').map((item, index) => diagnostic(item, `diagnostics[${index}]`)), capabilities: capabilities(data.capabilities, 'deal model current.capabilities') }
}

export function parseDealModelCatalogue(value: unknown): DealModelCatalogue {
  const data = closedRecord(envelopeData(value, 'deal model catalogue'), 'deal model catalogue.data', ['contractVersion', 'modelId', 'revision', 'sourceSha256', 'catalogueVersion', 'compilerValidationProfile', 'catalogueSha256', 'sourceFields', 'outputFields', 'functions', 'buildingBlocks'])
  contractVersion(data, 'deal-model-catalogue-v1', 'deal model catalogue')
  return {
    contractVersion: 'deal-model-catalogue-v1', modelId: uuid(data.modelId, 'catalogue.modelId'), revision: assertPositiveRevision(data.revision, 'catalogue.revision'), sourceSha256: sha256(data.sourceSha256, 'catalogue.sourceSha256'), catalogueVersion: stringValue(data.catalogueVersion, 'catalogue.catalogueVersion'), compilerValidationProfile: stringValue(data.compilerValidationProfile, 'catalogue.compilerValidationProfile'), catalogueSha256: sha256(data.catalogueSha256, 'catalogue.catalogueSha256'),
    sourceFields: arrayValue(data.sourceFields, 'catalogue.sourceFields').map((item, index) => catalogueField(item, `sourceFields[${index}]`) as DealModelCatalogueField),
    outputFields: arrayValue(data.outputFields, 'catalogue.outputFields').map((item, index) => catalogueField(item, `outputFields[${index}]`, true) as DealModelCatalogue['outputFields'][number]),
    functions: arrayValue(data.functions, 'catalogue.functions').map((item, index) => { const fn = closedRecord(item, `functions[${index}]`, ['name', 'signature', 'argumentTypes', 'returnType', 'deterministic', 'validationProfile']); return { name: stringValue(fn.name, `functions[${index}].name`), signature: stringValue(fn.signature, `functions[${index}].signature`), argumentTypes: arrayValue(fn.argumentTypes, `functions[${index}].argumentTypes`).map((value, i) => stringValue(value, `functions[${index}].argumentTypes[${i}]`)), returnType: stringValue(fn.returnType, `functions[${index}].returnType`), deterministic: booleanValue(fn.deterministic, `functions[${index}].deterministic`), validationProfile: stringValue(fn.validationProfile, `functions[${index}].validationProfile`) } }),
    buildingBlocks: arrayValue(data.buildingBlocks, 'catalogue.buildingBlocks').map((item, index) => { const block = closedRecord(item, `buildingBlocks[${index}]`, ['logicalModelId', 'version', 'sourceSha256', 'inputs', 'outputs', 'owner', 'visibility']); return { logicalModelId: uuid(block.logicalModelId, `buildingBlocks[${index}].logicalModelId`), version: stringValue(block.version, `buildingBlocks[${index}].version`), sourceSha256: sha256(block.sourceSha256, `buildingBlocks[${index}].sourceSha256`), inputs: arrayValue(block.inputs, `buildingBlocks[${index}].inputs`).map((value, i) => catalogueField(value, `buildingBlocks[${index}].inputs[${i}]`) as DealModelCatalogueField), outputs: arrayValue(block.outputs, `buildingBlocks[${index}].outputs`).map((value, i) => catalogueField(value, `buildingBlocks[${index}].outputs[${i}]`) as DealModelCatalogueField), owner: stringValue(block.owner, `buildingBlocks[${index}].owner`), visibility: enumValue(block.visibility, `buildingBlocks[${index}].visibility`, ['system', 'private', 'shared']) } }),
  }
}

export function parseDealModelFixtures(value: unknown): DealModelFixtureList {
  const data = closedRecord(envelopeData(value, 'deal model fixtures'), 'deal model fixtures.data', ['contractVersion', 'logicalModelId', 'revision', 'sourceSha256', 'items', 'capabilities'])
  contractVersion(data, 'deal-model-fixtures-v1', 'deal model fixtures')
  return { contractVersion: 'deal-model-fixtures-v1', logicalModelId: uuid(data.logicalModelId, 'fixtures.logicalModelId'), revision: assertPositiveRevision(data.revision, 'fixtures.revision'), sourceSha256: sha256(data.sourceSha256, 'fixtures.sourceSha256'), items: arrayValue(data.items, 'fixtures.items').map((item, index) => fixture(item, `fixtures.items[${index}]`)), capabilities: capabilities(data.capabilities, 'fixtures.capabilities') }
}

interface FixtureMutationCommon { contractVersion: 'deal-model-fixture-mutation-v1'; logicalModelId: string; revision: string; sourceSha256: string; capabilities: DealModelCapabilities }
export interface FixtureUpsertMutationOutcome extends FixtureMutationCommon { fixture: DealModelFixture }
export type FixtureDeleteMutationOutcome = FixtureMutationCommon
export function parseFixtureMutation(value: unknown, variant: 'upsert'): FixtureUpsertMutationOutcome
export function parseFixtureMutation(value: unknown, variant: 'delete'): FixtureDeleteMutationOutcome
export function parseFixtureMutation(value: unknown, variant: 'upsert' | 'delete'): FixtureUpsertMutationOutcome | FixtureDeleteMutationOutcome {
  const data = closedRecord(envelopeData(value, 'fixture mutation'), 'fixture mutation.data', variant === 'upsert' ? ['contractVersion', 'logicalModelId', 'revision', 'sourceSha256', 'fixture', 'capabilities'] : ['contractVersion', 'logicalModelId', 'revision', 'sourceSha256', 'capabilities'])
  contractVersion(data, 'deal-model-fixture-mutation-v1', 'fixture mutation')
  const common = { contractVersion: 'deal-model-fixture-mutation-v1' as const, logicalModelId: uuid(data.logicalModelId, 'fixture mutation.logicalModelId'), revision: assertPositiveRevision(data.revision, 'fixture mutation.revision'), sourceSha256: sha256(data.sourceSha256, 'fixture mutation.sourceSha256'), capabilities: capabilities(data.capabilities, 'fixture mutation.capabilities') }
  return variant === 'upsert' ? { ...common, fixture: fixture(data.fixture, 'fixture mutation.fixture') } : common
}

export interface FixtureRunOutcome { contractVersion: 'deal-model-fixture-run-v1'; logicalModelId: string; revision: string; runId: string; requestFingerprint: string; fixtureId: string | null; traceLevel: 'none' | 'summary' | 'full'; watchedOutputs: string[]; passed: number; failed: number; outcomes: Array<{ fixtureId: string; status: 'passed' | 'mismatch' | 'evaluationFailed'; resultSha256: string | null }>; capabilities: DealModelCapabilities }
export function parseFixtureRun(value: unknown): FixtureRunOutcome {
  const data = closedRecord(envelopeData(value, 'fixture run'), 'fixture run.data', ['contractVersion', 'logicalModelId', 'revision', 'runId', 'requestFingerprint', 'fixtureId', 'traceLevel', 'watchedOutputs', 'passed', 'failed', 'outcomes', 'capabilities'])
  contractVersion(data, 'deal-model-fixture-run-v1', 'fixture run')
  return { contractVersion: 'deal-model-fixture-run-v1', logicalModelId: uuid(data.logicalModelId, 'fixture run.logicalModelId'), revision: assertPositiveRevision(data.revision, 'fixture run.revision'), runId: uuid(data.runId, 'fixture run.runId'), requestFingerprint: uuid(data.requestFingerprint, 'fixture run.requestFingerprint'), fixtureId: nullableUuid(data.fixtureId, 'fixture run.fixtureId'), traceLevel: enumValue(data.traceLevel, 'fixture run.traceLevel', ['none', 'summary', 'full']), watchedOutputs: arrayValue(data.watchedOutputs, 'fixture run.watchedOutputs').map((item, index) => pointer(item, `watchedOutputs[${index}]`)), passed: safeInteger(data.passed, 'fixture run.passed', 0), failed: safeInteger(data.failed, 'fixture run.failed', 0), outcomes: arrayValue(data.outcomes, 'fixture run.outcomes').map((item, index) => { const outcome = closedRecord(item, `outcomes[${index}]`, ['fixtureId', 'status', 'resultSha256']); return { fixtureId: uuid(outcome.fixtureId, `outcomes[${index}].fixtureId`), status: enumValue(outcome.status, `outcomes[${index}].status`, ['passed', 'mismatch', 'evaluationFailed']), resultSha256: nullableSha256(outcome.resultSha256, `outcomes[${index}].resultSha256`) } }), capabilities: capabilities(data.capabilities, 'fixture run.capabilities') }
}

export function parseDealModelPreview(value: unknown): DealModelPreview {
  const data = closedRecord(envelopeData(value, 'preview'), 'preview.data', ['contractVersion', 'logicalModelId', 'revision', 'requestFingerprint', 'sourceSha256', 'resultSha256', 'calculationState', 'readinessState', 'result', 'diagnostics', 'traceAvailable', 'elapsedMs', 'completedAt'])
  contractVersion(data, 'deal-model-preview-v1', 'preview')
  return {
    contractVersion: 'deal-model-preview-v1',
    logicalModelId: uuid(data.logicalModelId, 'preview.logicalModelId'),
    revision: assertPositiveRevision(data.revision, 'preview.revision'),
    requestFingerprint: uuid(data.requestFingerprint, 'preview.requestFingerprint'),
    sourceSha256: sha256(data.sourceSha256, 'preview.sourceSha256'),
    resultSha256: nullableSha256(data.resultSha256, 'preview.resultSha256'),
    calculationState: enumValue(data.calculationState, 'preview.calculationState', ['computed', 'notComputable', 'evaluationFailed']),
    readinessState: enumValue(data.readinessState, 'preview.readinessState', ['ready', 'readyWithWarnings', 'missingInputs', 'ineligible', 'notApplicable', 'failed']),
    result: data.result === null ? null : assertJsonUtf8Ceiling(objectValue(data.result, 'preview.result'), 'preview.result', DEAL_MODEL_RESOURCE_LIMITS.evaluationOutputUtf8Bytes),
    diagnostics: arrayValue(data.diagnostics, 'preview.diagnostics').map((item, index) => diagnostic(item, `preview.diagnostics[${index}]`)),
    traceAvailable: booleanValue(data.traceAvailable, 'preview.traceAvailable'),
    elapsedMs: safeInteger(data.elapsedMs, 'preview.elapsedMs', 0),
    completedAt: timestamp(data.completedAt, 'preview.completedAt'),
  }
}

export function parseCloneOutcome(value: unknown, expected: { sourceModelId: string; idempotencyKey: string }): DealModelCloneOutcome { const data = closedRecord(envelopeData(value, 'clone'), 'clone.data', ['contractVersion', 'logicalModelId', 'cloneOfLogicalModelId', 'revision', 'sourceSha256', 'owner', 'visibility', 'idempotencyKey', 'capabilities', 'createdAt']); contractVersion(data, 'deal-model-clone-v1', 'clone'); const parsed = { contractVersion: 'deal-model-clone-v1' as const, logicalModelId: uuid(data.logicalModelId, 'clone.logicalModelId'), cloneOfLogicalModelId: uuid(data.cloneOfLogicalModelId, 'clone.cloneOfLogicalModelId'), revision: assertPositiveRevision(data.revision, 'clone.revision'), sourceSha256: sha256(data.sourceSha256, 'clone.sourceSha256'), owner: stringValue(data.owner, 'clone.owner'), visibility: enumValue(data.visibility, 'clone.visibility', ['private'] as const), idempotencyKey: uuid(data.idempotencyKey, 'clone.idempotencyKey'), capabilities: capabilities(data.capabilities, 'clone.capabilities'), createdAt: timestamp(data.createdAt, 'clone.createdAt') }; if (parsed.cloneOfLogicalModelId !== expected.sourceModelId || parsed.idempotencyKey !== expected.idempotencyKey) mismatch('clone identity echo mismatch'); return parsed }

/// The receipt for Reset to built-in. Closed and identity-checked like every
/// other receipt: the model and the idempotency key must echo the request, and
/// the revision and source identity say what the draft now serves.
export function parseResetOutcome(value: unknown, expected: { logicalModelId: string; idempotencyKey: string }): DealModelResetOutcome {
  const data = closedRecord(envelopeData(value, 'reset'), 'reset.data', ['contractVersion', 'logicalModelId', 'revision', 'sourceSha256', 'idempotencyKey', 'capabilities'])
  contractVersion(data, 'deal-model-reset-v1', 'reset')
  const parsed: DealModelResetOutcome = {
    contractVersion: 'deal-model-reset-v1',
    logicalModelId: uuid(data.logicalModelId, 'reset.logicalModelId'),
    revision: assertPositiveRevision(data.revision, 'reset.revision'),
    sourceSha256: sha256(data.sourceSha256, 'reset.sourceSha256'),
    idempotencyKey: uuid(data.idempotencyKey, 'reset.idempotencyKey'),
    capabilities: capabilities(data.capabilities, 'reset.capabilities'),
  }
  if (parsed.logicalModelId !== expected.logicalModelId || parsed.idempotencyKey !== expected.idempotencyKey) mismatch('reset request identity mismatch')
  return parsed
}

function userSettingsData(value: unknown): DealModelUserSettings {
  const present = envelopeData(value, 'user settings')
  const fields = ['contractVersion', 'settingsRevision', 'defaultAssumptions', 'defaultAssumptionDescriptors', 'effectivePermissions', 'updatedAt']
  const data = closedRecord(present, 'user settings.data', 'sourceDefaultAssumptions' in present ? [...fields, 'sourceDefaultAssumptions'] : fields)
  contractVersion(data, 'deal-model-user-settings-v2', 'user settings')
  const settingsRevision = assertSettingsRevision(data.settingsRevision, 'user settings.settingsRevision')
  const permissions = objectValue(data.effectivePermissions, 'user settings.effectivePermissions')
  const descriptors = arrayValue(data.defaultAssumptionDescriptors, 'user settings.defaultAssumptionDescriptors').map((item, index) => valueDescriptor(item, `user settings.defaultAssumptionDescriptors[${index}]`))
  const effectivePermissions = Object.fromEntries(Object.entries(permissions).map(([key, item]) => [key, booleanValue(item, `effectivePermissions.${key}`)]))
  const sourceDefaultAssumptions = sparseScalarRecord(data.sourceDefaultAssumptions === undefined ? {} : data.sourceDefaultAssumptions, descriptors, 'user settings.sourceDefaultAssumptions')
  if (settingsRevision === '0') {
    if (data.updatedAt !== null || ownKeys(objectValue(data.defaultAssumptions, 'user settings.defaultAssumptions')).length !== 0) mismatch('unconfigured user settings must have defaultAssumptions={} and updatedAt=null')
    return { contractVersion: 'deal-model-user-settings-v2', settingsRevision: '0', defaultAssumptions: {}, sourceDefaultAssumptions, defaultAssumptionDescriptors: descriptors, effectivePermissions, updatedAt: null }
  }
  return { contractVersion: 'deal-model-user-settings-v2', settingsRevision, defaultAssumptions: sparseScalarRecord(data.defaultAssumptions, descriptors, 'user settings.defaultAssumptions'), sourceDefaultAssumptions, defaultAssumptionDescriptors: descriptors, effectivePermissions, updatedAt: timestamp(data.updatedAt, 'user settings.updatedAt') }
}
export const parseDealModelUserSettings = userSettingsData

export function parseDealModelSettings(value: unknown): DealModelSettings { const data = closedRecord(envelopeData(value, 'model settings'), 'model settings.data', ['contractVersion', 'logicalModelId', 'settingsRevision', 'values', 'valueDescriptors', 'capabilities', 'updatedAt']); contractVersion(data, 'deal-model-settings-v1', 'model settings'); const descriptors = arrayValue(data.valueDescriptors, 'model settings.valueDescriptors').map((item, index) => valueDescriptor(item, `model settings.valueDescriptors[${index}]`)); return { contractVersion: 'deal-model-settings-v1', logicalModelId: uuid(data.logicalModelId, 'model settings.logicalModelId'), settingsRevision: assertSettingsRevision(data.settingsRevision, 'model settings.settingsRevision'), values: exactScalarRecord(data.values, descriptors, 'model settings.values'), valueDescriptors: descriptors, capabilities: capabilities(data.capabilities, 'model settings.capabilities'), updatedAt: timestamp(data.updatedAt, 'model settings.updatedAt') } }

function resultOutputDescriptor(value: unknown, field: string): PropertyDealModelChoice['presentation']['outputs'][number] {
  const data = closedRecord(value, field, ['outputId', 'key', 'label', 'pointer', 'semanticType', 'unit', 'nullable', 'enumValues'])
  const semanticType = enumValue(data.semanticType, `${field}.semanticType`, SEMANTIC_TYPES)
  const enumValues = arrayValue(data.enumValues, `${field}.enumValues`).map((item, index) => stringValue(item, `${field}.enumValues[${index}]`))
  if (semanticType === 'enum' ? enumValues.length === 0 : enumValues.length > 0) mismatch(`${field}.enumValues does not match semanticType`)
  const parsedPointer = pointer(data.pointer, `${field}.pointer`)
  if (!parsedPointer.startsWith('/outputs/') && !/^\/deal\/[^/]+$/.test(parsedPointer)) mismatch(`${field}.pointer must identify a declared output or main outcome leaf`)
  return {
    outputId: uuid(data.outputId, `${field}.outputId`),
    key: keyValue(data.key, `${field}.key`),
    label: stringValue(data.label, `${field}.label`),
    pointer: parsedPointer,
    semanticType,
    unit: nullableString(data.unit, `${field}.unit`),
    nullable: booleanValue(data.nullable, `${field}.nullable`),
    enumValues,
  }
}

function resultPresentation(value: unknown, field: string): PropertyDealModelChoice['presentation'] {
  const data = closedRecord(value, field, ['headlineOutputIds', 'outputs'])
  const outputs = arrayValue(data.outputs, `${field}.outputs`).map((output, index) => resultOutputDescriptor(output, `${field}.outputs[${index}]`))
  const headlineOutputIds = arrayValue(data.headlineOutputIds, `${field}.headlineOutputIds`).map((id, index) => uuid(id, `${field}.headlineOutputIds[${index}]`))
  for (const [name, values] of Object.entries({ outputId: outputs.map(output => output.outputId), key: outputs.map(output => output.key), pointer: outputs.map(output => output.pointer), headlineOutputId: headlineOutputIds })) {
    if (new Set(values).size !== values.length) mismatch(`${field} has duplicate ${name} values`)
  }
  if (headlineOutputIds.some(id => !outputs.some(output => output.outputId === id))) mismatch(`${field} has a detached headline output`)
  return { headlineOutputIds, outputs }
}

export async function parseSelectedDealModels(value: unknown): Promise<SelectedDealModel[]> {
  const entries = arrayValue(value, 'selectedDealModels')
  if (entries.length > 2) mismatch('selectedDealModels permits at most two models')
  const models = await Promise.all(entries.map(async (entry, index): Promise<SelectedDealModel> => {
    const field = `selectedDealModels[${index}]`
    const data = closedRecord(entry, field, ['logicalModelId', 'modelName', 'ownership', 'current'])
    const logicalModelId = uuid(data.logicalModelId, `${field}.logicalModelId`)
    const modelName = nullableString(data.modelName, `${field}.modelName`)
    const ownership = data.ownership === null ? null : enumValue(data.ownership, `${field}.ownership`, ['builtIn', 'private'])
    if ((modelName === null) !== (ownership === null)) mismatch(`${field} visibility metadata must be consistently absent`)
    if (data.current === null) return { logicalModelId, modelName, ownership, current: null }
    if (modelName === null) mismatch(`${field} current model requires visible metadata`)
    const row = closedRecord(data.current, `${field}.current`, ['definitionRevision', 'sourceSha256', 'presentationIdentitySha256', 'presentation', 'verdictOutputId', 'headlineOutputId'])
    const presentation = resultPresentation(row.presentation, `${field}.current.presentation`)
    const current: NonNullable<SelectedDealModel['current']> = {
      definitionRevision: safeInteger(row.definitionRevision, `${field}.definitionRevision`, 1),
      sourceSha256: sha256(row.sourceSha256, `${field}.sourceSha256`), presentationIdentitySha256: sha256(row.presentationIdentitySha256, `${field}.presentationIdentitySha256`),
      presentation,
      verdictOutputId: nullableUuid(row.verdictOutputId, `${field}.verdictOutputId`), headlineOutputId: nullableUuid(row.headlineOutputId, `${field}.headlineOutputId`),
    }
    const ids = [current.verdictOutputId, current.headlineOutputId].filter((id): id is string => id !== null)
    if (new Set(ids).size !== ids.length || ids.some(id => !presentation.headlineOutputIds.includes(id))) mismatch(`${field} compact output IDs must be distinct declared headlines`)
    if (current.verdictOutputId !== null && presentation.outputs.find(output => output.outputId === current.verdictOutputId)?.semanticType !== 'enum') mismatch(`${field} verdict must be a declared enum`)
    return { logicalModelId, modelName, ownership, current }
  }))
  if (new Set(models.map(model => model.logicalModelId)).size !== models.length) mismatch('selectedDealModels has duplicate model IDs')
  return models
}

export function validateExactRequestValues(value: Record<string, string | boolean | null>, descriptors: DealModelValueDescriptor[], field: string): Record<string, string | boolean | null> { return exactScalarRecord(value, descriptors, field) }
export function validateUserSettingsRequestValues(value: Record<string, string | boolean | null>, descriptors: DealModelValueDescriptor[], field: string): Record<string, string | boolean | null> { return sparseScalarRecord(value, descriptors, field) }
